import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Eye, EyeOff, Flag, MoreHorizontal, Reply, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../../../contexts/AuthContext';
import {
  deleteComment,
  removeComment,
  reportComment,
  restoreComment,
} from '../../../../database/newsComments';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import NewsCommentCompose from './NewsCommentCompose';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const formatRelative = (ts) => {
  if (!ts?.toDate) return '';
  const d = ts.toDate();
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

const NewsCommentItem = ({ comment, newsId, canModerate }) => {
  const { currentUser } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [replying, setReplying] = useState(false);

  const replies = comment.replies || [];
  const isAuthor = currentUser?.uid === comment.authorUserId;
  const isHidden = comment.status === 'hidden';
  const isRemoved = comment.status === 'removed';
  const isPlaceholder = !!comment.placeholder; // synthetic node for a dropped parent
  const reportedByMe = !!currentUser && (comment.reportedBy || []).includes(currentUser.uid);

  // For non-mod reporters, hidden comments default to a placeholder; reveal flips it open.
  const hideTextForReporter = isHidden && reportedByMe && !canModerate && !revealed;

  const canDelete = isAuthor || canModerate;
  const canReport = !!currentUser && !isAuthor && comment.status === 'visible';
  const canReply = !!currentUser && comment.status === 'visible' && !isPlaceholder;

  const run = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
    } catch (err) {
      toast.error(failMsg, { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this comment?')) return;
    run(() => deleteComment(comment.id), 'Comment deleted.', 'Delete failed.');
  };
  const handleReport = () =>
    run(
      () => reportComment(comment.id, currentUser.uid),
      'Comment reported. Moderators will review it.',
      'Report failed.'
    );
  const handleRemove = () => {
    if (!window.confirm('Remove this comment? It stays archived but hidden from users.')) return;
    run(() => removeComment(comment.id, currentUser?.uid), 'Comment removed.', 'Remove failed.');
  };
  const handleRestore = () =>
    run(() => restoreComment(comment.id), 'Comment restored.', 'Restore failed.');

  const RepliesList = replies.length > 0 && (
    <ul className="mt-3 space-y-3 border-l border-border/60 pl-3 sm:pl-4">
      {replies.map((child) => (
        <NewsCommentItem key={child.id} comment={child} newsId={newsId} canModerate={canModerate} />
      ))}
    </ul>
  );

  // Synthetic placeholder for a parent that was removed/hidden but still has visible replies.
  if (isPlaceholder) {
    return (
      <li>
        <p className="flex items-center gap-2 py-2 text-sm italic text-muted-foreground">
          <EyeOff className="h-3.5 w-3.5" />
          This comment is no longer available.
        </p>
        {RepliesList}
      </li>
    );
  }

  // Compact placeholder shown to the reporter when a comment is hidden pending review.
  if (hideTextForReporter) {
    return (
      <li>
        <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-2">
            <EyeOff className="h-3.5 w-3.5" />
            You reported this comment. It's hidden pending review.
          </span>
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:border-foreground/40 hover:text-foreground"
          >
            <Eye className="h-3 w-3" />
            Show
          </button>
        </div>
        {RepliesList}
      </li>
    );
  }

  return (
    <li>
      <div className="grid grid-cols-[auto_1fr] gap-3">
        <Link to={`/user/${comment.authorUserId}`} className="shrink-0">
          <Avatar className="h-9 w-9">
            {comment.authorPhotoURL && <AvatarImage src={comment.authorPhotoURL} alt="" />}
            <AvatarFallback className="text-xs">{initials(comment.authorUsername)}</AvatarFallback>
          </Avatar>
        </Link>

        <div className="min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Link
                to={`/user/${comment.authorUserId}`}
                className="truncate font-display text-sm font-medium hover:underline"
              >
                {comment.authorUsername || 'Player'}
              </Link>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {formatRelative(comment.createdAt)}
              </span>
              {isHidden && (
                <Badge variant="outline" className="text-warning">
                  Hidden · pending review
                </Badge>
              )}
              {isRemoved && (
                <Badge variant="outline" className="text-destructive">
                  Removed by moderator
                </Badge>
              )}
            </div>

            {(canReport || canDelete || canModerate || (isHidden && reportedByMe)) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={busy}
                    className="rounded-md p-1 text-muted-foreground hover:bg-secondary/60 hover:text-foreground disabled:opacity-50"
                    aria-label="Comment actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canReport && (
                    <DropdownMenuItem onSelect={handleReport}>
                      <Flag className="mr-2 h-3.5 w-3.5" />
                      Report
                    </DropdownMenuItem>
                  )}
                  {isHidden && reportedByMe && !canModerate && (
                    <DropdownMenuItem onSelect={() => setRevealed(false)}>
                      <EyeOff className="mr-2 h-3.5 w-3.5" />
                      Hide again
                    </DropdownMenuItem>
                  )}
                  {canModerate && isHidden && (
                    <DropdownMenuItem onSelect={handleRestore}>
                      <Check className="mr-2 h-3.5 w-3.5" />
                      Restore
                    </DropdownMenuItem>
                  )}
                  {canModerate && !isRemoved && (
                    <DropdownMenuItem onSelect={handleRemove}>
                      <EyeOff className="mr-2 h-3.5 w-3.5" />
                      Remove (keep archived)
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <DropdownMenuItem onSelect={handleDelete} className="text-destructive">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {isRemoved ? (
            <p className="mt-1 text-sm italic text-muted-foreground">
              This comment was removed by a moderator.
            </p>
          ) : (
            <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">
              {comment.text}
            </p>
          )}

          {!isRemoved && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
              {canReply && (
                <button
                  type="button"
                  onClick={() => setReplying((v) => !v)}
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Reply className="h-3 w-3" />
                  Reply
                </button>
              )}
            </div>
          )}

          {replying && (
            <div className="mt-3">
              <NewsCommentCompose
                newsId={newsId}
                parentId={comment.id}
                autoFocus
                onDone={() => setReplying(false)}
              />
            </div>
          )}
        </div>
      </div>

      {RepliesList}
    </li>
  );
};

export default NewsCommentItem;
